import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";

// Sticky banner that appears when any query last finished with a network /
// 5xx error. Cleared once any query succeeds. Cheap, transient signal — no
// retry button (TanStack Query already retries). Network outages on the
// real backend are common; surfacing them avoids the "stuck spinner" UX.
export function NetworkBanner(): JSX.Element | null {
  const queryClient = useQueryClient();
  const [issue, setIssue] = useState<{ kind: "network" | "server"; message: string } | null>(null);

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsub = cache.subscribe((evt) => {
      if (evt.type !== "updated") return;
      const state = evt.query.state;
      if (state.status === "error") {
        const err = state.error;
        if (err instanceof ApiError) {
          if (err.status === 0) {
            setIssue({ kind: "network", message: "Network connection lost. Retrying…" });
            return;
          }
          if (err.status >= 500) {
            setIssue({ kind: "server", message: "The server hit an error. Please try again." });
            return;
          }
        }
      }
      if (state.status === "success") {
        setIssue(null);
      }
    });
    return () => unsub();
  }, [queryClient]);

  if (!issue) return null;
  return (
    <div className="global-banner" role="status" data-testid="network-banner">
      <span style={{ fontWeight: 600 }}>•</span>
      <span>{issue.message}</span>
    </div>
  );
}
